import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Map as LeafletMap, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { driverRecordShape } from './driverRecordShape';
import { computeBoundsForDrivers, computeBoundsForPoints } from './mapGeometry';
import { FALLBACK_CENTER, FALLBACK_ZOOM } from './mapConfig';
import { TILE_URL, TILE_ATTRIBUTION } from '../../../utils/leaflet';
import useLeafletAutoInvalidate from '../../../hooks/useLeafletAutoInvalidate';

// The one Leaflet viewport both Monitoring map panes render into. Owns the
// basemap, the camera, and the marker pulse keyframes; panes pass their
// layers as children. Camera policy: frame `fitTargets` once at mount, again
// on a user action — `fitKey` changing (route selection, order toggle, Apply
// Filters) or a click on the Recenter button — and never on a poll tick, so
// drivers moving between polls don't yank the view.

const FIT_PADDING = [40, 40];
const SINGLE_POINT_ZOOM = 13;
// A focused stop should fill the viewport with its neighborhood, but never
// zoom the supervisor back OUT of a closer look they already took.
const FOCUS_MIN_ZOOM = 16;
// One shared object: react-leaflet 2 refits the map whenever the
// `boundsOptions` prop is a different object (Map.js updateLeafletElement),
// so an inline literal here would re-run fitBounds on every render — i.e.
// on every poll tick — and undo the supervisor's zoom.
const FIT_BOUNDS_OPTIONS = { padding: FIT_PADDING };

// Camera target for whatever the pane framed: bare points when given (the
// driver day view's readings and stops), else driver records.
function boundsFor({ fitPoints, fitTargets }) {
  return fitPoints
    ? computeBoundsForPoints(fitPoints)
    : computeBoundsForDrivers(fitTargets);
}

// Shared by the fitKey effect and the Recenter button: frame the map on
// whatever `targets` currently holds.
function fitTo(mapEl, targets) {
  if (!mapEl) {
    return;
  }
  const target = boundsFor(targets);
  if (!target) {
    return;
  }
  if (target.bounds) {
    mapEl.fitBounds(target.bounds, FIT_BOUNDS_OPTIONS);
  } else if (target.single) {
    mapEl.setView(target.single, SINGLE_POINT_ZOOM);
  }
}

const StyledDiv = styledComponent('div', {
  position: 'relative',
  flex: 1,
  minHeight: theme.sizes.mapMinHeight,
  overflow: 'hidden',

  // Absolute fill, not `height: 100%`: a percentage height only resolves
  // when the parent's height is definite, and this shell often sits in a
  // flex column whose height comes from min-height alone (RouteMapPane).
  // There `100%` computed to 0 and Leaflet drew nothing. Filling the
  // positioned shell box works in every pane layout.
  '& .leaflet-container': {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    background: theme.colors.bgMuted,
    fontFamily: 'inherit',
  },
  // Mute the basemap so the route layers carry the color.
  '& .leaflet-tile-pane': {
    filter: theme.filters.mutedBasemap,
  },
  '& .leaflet-popup-content-wrapper': {
    borderRadius: theme.radii.inner,
    padding: 0,
    boxShadow: theme.shadows.dropdown,
  },
  '& .leaflet-popup-content': {
    margin: 0,
    minWidth: '200px',
    fontFamily: 'inherit',
  },
  '& .mt-driver-marker, & .mt-stop-marker, & .mt-start-marker, & .mt-gap-marker':
    {
      background: 'transparent',
      border: 'none',
    },
  // Trail inspection surfaces (GPS-gap labels, the hover card) ride in
  // Leaflet tooltips; the pane's own `rmp-` classes style their contents.
  '& .leaflet-tooltip': {
    padding: theme.spacing.tooltipPadding,
    background: theme.colors.bgCard,
    border: `1px solid ${theme.colors.borderLight}`,
    borderRadius: theme.radii.inner,
    boxShadow: theme.shadows.dropdown,
    color: theme.colors.textBody,
    fontFamily: 'inherit',
    fontSize: theme.sizes.captionFontSize,
    whiteSpace: 'nowrap',
  },
  '& .leaflet-tooltip::before': {
    display: 'none',
  },
  '& .mms-recenter': {
    position: 'absolute',
    right: theme.spacing.gapLg,
    bottom: theme.spacing.sectionGap,
    zIndex: 1000,
    width: theme.sizes.mapControlSize,
    height: theme.sizes.mapControlSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    background: theme.colors.bgCard,
    border: theme.borders.input,
    borderRadius: theme.radii.input,
    boxShadow: theme.shadows.card,
    color: theme.colors.textBody,
    cursor: 'pointer',
  },
  '& .mms-recenter:hover': {
    background: theme.colors.bgMuted,
  },
  '& .mms-recenter:focus-visible': {
    outline: 'none',
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
});

function MonitoringMapShell({
  fitTargets,
  fitPoints,
  fitKey,
  focusPoint,
  children,
}) {
  const mapRef = useRef(null);
  // Snapshotted once: the initial camera. Later refits go through the effect.
  const [initialBounds] = useState(() => boundsFor({ fitPoints, fitTargets }));
  const targetsRef = useRef({ fitPoints, fitTargets });
  targetsRef.current = { fitPoints, fitTargets };

  useLeafletAutoInvalidate(mapRef);

  useEffect(() => {
    const mapEl = mapRef.current ? mapRef.current.leafletElement : null;
    fitTo(mapEl, targetsRef.current);
  }, [fitKey]);

  // Focus move (a stop clicked in the Stops panel or a driver card): pan to
  // the point, zooming in to street level if the camera is wider than that.
  // Declared AFTER the fitKey effect so that on a shared commit (opening a
  // route straight onto a focused stop) the focus wins the camera. Keyed on
  // the point's coordinates, not the array identity, so poll re-renders that
  // rebuild the same point don't re-pan.
  const focusKey = focusPoint ? focusPoint.join(',') : null;
  useEffect(() => {
    const mapEl = mapRef.current ? mapRef.current.leafletElement : null;
    if (!mapEl || !focusKey) {
      return;
    }
    const point = focusKey.split(',').map(Number);
    mapEl.setView(point, Math.max(mapEl.getZoom(), FOCUS_MIN_ZOOM), {
      animate: true,
    });
  }, [focusKey]);

  const handleRecenter = () => {
    const mapEl = mapRef.current ? mapRef.current.leafletElement : null;
    fitTo(mapEl, targetsRef.current);
  };

  let mapInitProps;
  if (initialBounds && initialBounds.bounds) {
    mapInitProps = {
      bounds: initialBounds.bounds,
      boundsOptions: FIT_BOUNDS_OPTIONS,
    };
  } else if (initialBounds && initialBounds.single) {
    mapInitProps = { center: initialBounds.single, zoom: SINGLE_POINT_ZOOM };
  } else {
    mapInitProps = { center: FALLBACK_CENTER, zoom: FALLBACK_ZOOM };
  }

  return (
    <StyledDiv className="mms-map">
      {/* divIcons render outside React's tree, so emotion's scoped keyframes
          can't reach them — a plain <style> keeps the name global. */}
      <style>
        {`@keyframes mt-halo-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.18); opacity: 0.55; }
        }`}
      </style>
      <LeafletMap
        ref={mapRef}
        bounds={mapInitProps.bounds}
        boundsOptions={mapInitProps.boundsOptions}
        center={mapInitProps.center}
        zoom={mapInitProps.zoom}
        minZoom={3}
        maxZoom={18}
        scrollWheelZoom
        preferCanvas
      >
        <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
        {children}
      </LeafletMap>
      <button
        type="button"
        className="mms-recenter"
        aria-label="Recenter map"
        title="Recenter map"
        onClick={handleRecenter}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="8" cy="8" r="2.25" fill="currentColor" />
          <path
            d="M8 0.5V3.5M8 12.5V15.5M0.5 8H3.5M12.5 8H15.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </StyledDiv>
  );
}

MonitoringMapShell.propTypes = {
  // What the camera frames: driver records (positions + stops), or, when
  // given, bare {latitude, longitude} points instead.
  fitTargets: PropTypes.arrayOf(driverRecordShape),
  fitPoints: PropTypes.arrayOf(
    PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
  ),
  fitKey: PropTypes.string.isRequired,
  // [lat, lng] to pan/zoom the camera to — a stop the supervisor clicked.
  // Null leaves the camera to the fit policy above.
  focusPoint: PropTypes.arrayOf(PropTypes.number),
  children: PropTypes.node,
};

MonitoringMapShell.defaultProps = {
  fitTargets: [],
  fitPoints: null,
  focusPoint: null,
  children: null,
};

export default MonitoringMapShell;
